import {
  ChatStatus,
  SalesPortalProspectFinderNearbyBusinessesQueryVariables,
} from '@/types';
import { NetworkStatus } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSalesPortalProspectFinderNearbyBusinessesQuery } from '@/experiences/sales-portal/domain/prospects/blocs/documents/sales-portal-prospect-finder-nearby-businesses.generated';

export enum SortOrder {
  BEST_MATCH = 'best_match',
  DISTANCE = 'distance',
  RATING = 'rating',
}

export const PAGE_SIZE = 20;
export const STARTING_OFFSET = 0;

export interface NearbyBusinessFilters {
  categories: string;
  isCurrentRestaurant?: boolean;
  latitude: number;
  longitude: number;
  price: string;
  radius: string | null;
  sortBy: SortOrder;
}

/**
 * Single source of truth for the query variables, so the cache entry the
 * claim mutator writes to always matches what this query reads.
 */
export const buildNearbyVariables = (
  supplierUUID: string,
  filters: NearbyBusinessFilters,
  offset: number = STARTING_OFFSET,
): SalesPortalProspectFinderNearbyBusinessesQueryVariables => ({
  ...filters,
  limit: PAGE_SIZE,
  offset,
  supplierUUID,
});

export interface NearbyBusiness {
  businessName: string;
  categories?: string[];
  displayAddress: string;
  distance?: number;
  imageUrl?: string;
  latitude: number;
  longitude: number;
  phoneNumber?: string;
  restaurantUuid?: string;
  status?: ChatStatus;
  supplierUuid?: string;
  yelpBusinessId: string;
}

/**
 * Loads Yelp-backed nearby businesses for the Prospect Finder with
 * infinite-scroll pagination; dedupes by `yelp_business_id`, stops on
 * sub-page-size response, resets on filter change.
 *
 * @param supplierUUID - Supplier whose country code joins the query for distance formatting.
 * @param filters - Lat/lng + categories/price/sort that constrain the Yelp result set.
 * @param options.enabled - When false, the query is skipped. Defaults to true.
 * @returns Apollo `error` / `loading`, the businesses array, `fetchMore` /
 * `fetchingMore` / `canFetchMore` for pagination, a `retry` refetch, and
 * `supplierCountryCode` (undefined when the query resolved but supplier
 * metadata was absent).
 */
export const useProspectFinderNearbyBusinessesLoader = (
  supplierUUID: string,
  filters: NearbyBusinessFilters,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  // Sync guard against rapid scroll events in the same tick
  const fetchingRef = useRef(false);
  const pageOffsetRef = useRef(STARTING_OFFSET);
  const [canFetchMore, setCanFetchMore] = useState(true);

  const { data, error, fetchMore, loading, networkStatus, refetch } =
    useSalesPortalProspectFinderNearbyBusinessesQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !enabled,
      variables: buildNearbyVariables(supplierUUID, filters),
    });

  const fetchingMore = networkStatus === NetworkStatus.fetchMore;
  const currentCount = data?.getNearbyBusinessesV2?.length ?? 0;
  const filtersSignature = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    setCanFetchMore(true);
    pageOffsetRef.current = STARTING_OFFSET;
  }, [filtersSignature]);

  // Stop paginating when the initial response is short; subsequent
  // pages are gated from inside updateQuery.
  useEffect(() => {
    if (!loading && currentCount > 0 && currentCount < PAGE_SIZE) {
      setCanFetchMore(false);
    }
  }, [loading, currentCount]);

  const handleFetchMore = useCallback(async () => {
    if (fetchingRef.current || !canFetchMore || fetchingMore || loading) {
      return;
    }
    fetchingRef.current = true;
    try {
      await fetchMore({
        updateQuery: (prevResult, { fetchMoreResult }) => {
          // Skip when prev data is empty — a filter-change refetch can be
          // in flight, leaving the cache without a page-1 to merge into.
          if (!fetchMoreResult || !prevResult?.getNearbyBusinessesV2?.length) {
            return prevResult;
          }
          setCanFetchMore(
            fetchMoreResult.getNearbyBusinessesV2.length === PAGE_SIZE,
          );
          pageOffsetRef.current += PAGE_SIZE;
          const merged = [
            ...prevResult.getNearbyBusinessesV2,
            ...fetchMoreResult.getNearbyBusinessesV2,
          ];
          // Yelp can return duplicates across pages when rankings shift mid-session.
          const seen = new Set<string>();
          const deduped = merged.filter((row) => {
            if (seen.has(row.yelp_business_id)) {
              return false;
            }
            seen.add(row.yelp_business_id);
            return true;
          });
          return {
            businesses_by_pk: prevResult.businesses_by_pk,
            getNearbyBusinessesV2: deduped,
          };
        },
        variables: buildNearbyVariables(
          supplierUUID,
          filters,
          pageOffsetRef.current + PAGE_SIZE,
        ),
      });
    } finally {
      fetchingRef.current = false;
    }
  }, [canFetchMore, fetchMore, fetchingMore, filters, loading, supplierUUID]);

  const nearbyBusinesses = useMemo<NearbyBusiness[] | undefined>(
    () =>
      data?.getNearbyBusinessesV2?.map((b) => ({
        businessName: b.business_name,
        categories: b.categories ?? undefined,
        displayAddress: b.business_display_address,
        distance: b.distance ?? undefined,
        imageUrl: b.image_url ?? undefined,
        latitude: b.latitude,
        longitude: b.longitude,
        phoneNumber: b.phone_number ?? undefined,
        restaurantUuid: b.restaurant_uuid ?? undefined,
        status: b.status ?? undefined,
        supplierUuid: b.supplier_uuid ?? undefined,
        yelpBusinessId: b.yelp_business_id,
      })),
    [data],
  );

  return {
    canFetchMore,
    error,
    fetchingMore,
    fetchMore: handleFetchMore,
    loading,
    nearbyBusinesses,
    retry: refetch,
    supplierCountryCode: data?.businesses_by_pk?.address.country_code,
  };
};
